import { useCallback, useEffect, useRef, useState } from 'react';
import { getProductInfo } from '../api';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

import theme from '../helpers/theme';
import getApiErrorMessage from '../helpers/apiErrorMessage';


export default function SearchBar({ setLoading, setError, token }) {
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const controllerRef = useRef(null);
  const verified = true;

  const navigate = useNavigate();

  const cancelSearch = useCallback(() => {
    if (!controllerRef.current) return;
    controllerRef.current.abort();
    controllerRef.current = null;
    setIsSearching(false);
    setLoading(false);
    setError(null);
  }, [setError, setLoading]);

  useEffect(() => {
    if (!isSearching) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') cancelSearch();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [cancelSearch, isSearching]);

  const handleSubmit = async () => {
    if (search.trim() === "" || controllerRef.current) return;

    const controller = new AbortController();
    controllerRef.current = controller;
    setIsSearching(true);
    setLoading(true);
    setError(null);
    try {
      const response = await getProductInfo(search, verified, controller.signal);
      const skus = response?.skus ?? [];

      if (!response && !isNaN(Number(search))) {
        setError("EAN inserido é invalido");
        return;
      }

      if (skus.length === 0) {
        setError("O produto não foi encontrado. No entanto ele foi adicionado em nossa fila prioritária, e, caso seja um produto válido, em breve estará disponível em nosso catálogo.");
        return;
      }

      if (skus.length > 1) {
        navigate(`/busca`, {
          state: {
            skus,
            scroll: response.id_scroll
          }
        });
      }
      else {
        navigate(`/produto/${skus[0]["id_sku"]}`, {
          state: {
            sku: skus[0],
          }
        });
      }
    } catch (error) {
      if (error.kind !== 'cancelled') {
        setError(getApiErrorMessage(error));
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setIsSearching(false);
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      // justifyContent: "center",
      alignItems: "center",
    }}>
    <Box
      sx={{
        "display": "flex",
        "width": { xs: "100%", sm: "auto" },
        "height": { xs: "auto", sm: "50px" },
        "flexDirection": { xs: "column", sm: "row" },
      }}
    >

    <Box
      component="form"
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
      noValidate
      autoComplete="off"
      sx={{
        "border": 1,
        "borderRadius": "200px",
        "borderColor": theme => theme.palette.border.main,
        "alignItems": "center",
        "justifyContent": "center",
        "width": { xs: "100%", sm: "460px" },
        "heigth": "50px",
        "paddingX": "16px"
      }}
    >

      <TextField 
        placeholder='Pesquise o produto pelo seu nome ou EAN'
        aria-label="Pesquisar produto"
        fullWidth
        disabled={isSearching}
        id="fullWidth"
        onChange={ e => setSearch(e.target.value) }
        variant="standard"
        InputProps={{
          disableUnderline: true,
          style: { fontSize: 16 },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{
          "marginTop": "9px"
        }}
      />

    </Box>

    <ThemeProvider theme={theme}>
      <Button
        variant={isSearching ? "outlined" : "contained"}
        disableRipple
        onClick={isSearching ? cancelSearch : handleSubmit}
        color="primary"
        sx={{
          "fontSize": 16,
          "borderRadius": "200px",
          "width": "120px",
          "marginLeft": { xs: 0, sm: "32px" },
          "marginTop": { xs: "16px", sm: 0 },
          "textTransform": "capitalize",
        }}
      >
        {isSearching ? 'Cancelar' : 'Pesquisar'}
      </Button>
    </ThemeProvider>
    </Box>

    </Box>
  );
}